/* eslint-disable no-unused-vars */
import _ from 'lodash';
import dateFns from 'date-fns';
import { formatDate } from '../utils/formatDate';
import { intervalsOverlap } from '../utils/intervalsOverlap';

/**
 * Verifica se um profissional possui períodos de visita agendados
 * (periodsForVisit) que se sobrepõem a outros períodos de visita
 * (periodsForVisit), AGREGANDO todos os períodos de todos os schedulingEvents
 * para o mesmo profissional.
 *
 * @param {Array<Object>} schedulingEvents - Array de objetos de agendamento.
 * Cada objeto deve ter:
 * - professionalName (string)
 * - periodsForVisit (Array<{start: string, end: string}>) - Visitas agendadas.
 * @returns {Array<string>} - Array de mensagens de erro para conflitos de agendamento de visita.
 */
function check(schedulingEvents) {
  const detailedErrors = [];
  const summaryErrors = [];

  // ==========================================================================
  // AGREGADOR: Map para armazenar todos os períodos de visita por profissional
  // ==========================================================================
  const professionalVisitsMap = new Map();

  // ===============================================================
  // 1. AGREGAÇÃO: Unir todos os períodos de visita por profissional
  // ===============================================================
  for (const event of schedulingEvents) {
    const { formVisits, professionalName, periodsForVisit } = event;
    const { professionalId } = formVisits;

    if (!professionalVisitsMap.has(professionalId)) {
      professionalVisitsMap.set(professionalId, {
        professionalName,
        visits: [],
        reportedConflicts: new Set() // Conjunto para evitar relatar a mesma visita conflitante várias vezes
      });
    }

    const professionalVisits = professionalVisitsMap.get(professionalId);
    professionalVisits.visits.push(...periodsForVisit);
  }

  // ==================================================
  // 2. COMPARAÇÃO: Comparar Visitas Agregadas entre si
  // ==================================================
  for (const [professionalId, data] of professionalVisitsMap.entries()) {
    const { visits, reportedConflicts } = data;
    const { professionalName } = data;

    // Loops aninhados para comparar cada visita (i) com todas as outras (j)
    for (let i = 0; i < visits.length; i++) {
      const visitA = visits[i];
      const startA = new Date(visitA.start);
      const endA = new Date(visitA.end);

      const keyA = `${visitA.start}|${visitA.end}`;

      for (let j = i + 1; j < visits.length; j++) {
        const visitB = visits[j];
        const startB = new Date(visitB.start);
        const endB = new Date(visitB.end);

        const keyB = `${visitB.start}|${visitB.end}`;

        // A chave de conflito é uma combinação ordenada das duas chaves de visita,
        // para garantir que A vs B seja reportado apenas uma vez e não A vs B e B vs A.
        const conflictKey = keyA < keyB ? `${keyA}_${keyB}` : `${keyB}_${keyA}`;

        // ===================================
        // 3. Verificar Sobreposição
        // ===================================
        if (intervalsOverlap(startA, endA, startB, endB)) {
          if (!reportedConflicts.has(conflictKey)) {
            // Formata a mensagem de erro
            const errorMessage =
              `Conflito de visitas agendadas para o profissional ${professionalName}: ` +
              `Visita 1 (${formatDate(visitA.start)} das ${dateFns.format(
                startA,
                'HH:mm'
              )} às ${dateFns.format(endA, 'HH:mm')}) ` +
              `e Visita 2 (${formatDate(visitB.start)} das ${dateFns.format(
                startB,
                'HH:mm'
              )} às ${dateFns.format(endB, 'HH:mm')}) se sobrepõem.`;

            const aggregateErrorsMsg = `Existe(m) conflito(s) de visitas agendadas para o profissional ${professionalName}`;
            if (!summaryErrors.find(err => err === aggregateErrorsMsg)) {
              summaryErrors.push(aggregateErrorsMsg);
            }

            detailedErrors.push(errorMessage);
            reportedConflicts.add(conflictKey);
          }
        }
      }
    }
  }

  return { detailedErrors, summaryErrors };
}

export { check };
